import { useState, useEffect } from 'react';
import { getCreditPaymentSummary, editCreditPayment } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { CreditCard, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Printer, Building2, Calendar, Pencil, Check, X } from 'lucide-react';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

const PAYMENT_COLORS = {
  '月掛け': 'bg-blue-100 text-blue-800 border-blue-300',
  '15日掛け': 'bg-purple-100 text-purple-800 border-purple-300',
  '翌週掛け': 'bg-teal-100 text-teal-800 border-teal-300',
};

export const CreditPayments = () => {
  const [currentMonth, setCurrentMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [data, setData] = useState({ suppliers: [], grand_total: 0 });
  const [loading, setLoading] = useState(true);
  const [expandedSupplier, setExpandedSupplier] = useState(null);
  const [editingRow, setEditingRow] = useState(null); // { supplierId, date }
  const [editValues, setEditValues] = useState({ amount_8: '', amount_10: '' });

  useEffect(() => { fetchData(); }, [currentMonth]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getCreditPaymentSummary(currentMonth);
      setData(res.data);
    } catch (error) {
      toast.error('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const changeMonth = (delta) => {
    const [y, m] = currentMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setCurrentMonth(format(d, 'yyyy-MM'));
    setExpandedSupplier(null);
    setEditingRow(null);
  };

  const monthDisplay = (() => {
    const [y, m] = currentMonth.split('-').map(Number);
    return format(new Date(y, m - 1, 1), 'yyyy年M月', { locale: ja });
  })();

  const toggleExpand = (supplierId) => {
    setExpandedSupplier(prev => prev === supplierId ? null : supplierId);
    setEditingRow(null);
  };

  const startEdit = (supplierId, day) => {
    const total8 = day.items.reduce((s, i) => s + (i.amount_8 || 0), 0);
    const total10 = day.items.reduce((s, i) => s + (i.amount_10 || 0), 0);
    setEditingRow({ supplierId, date: day.date });
    setEditValues({ amount_8: total8.toString(), amount_10: total10.toString() });
  };

  const cancelEdit = () => {
    setEditingRow(null);
  };

  const saveEdit = async (supplierId) => {
    try {
      await editCreditPayment({
        date: editingRow.date,
        supplier_id: supplierId,
        amount_8: parseFloat(editValues.amount_8) || 0,
        amount_10: parseFloat(editValues.amount_10) || 0,
        item_index: 0,
      });
      toast.success('更新しました');
      setEditingRow(null);
      fetchData();
    } catch (error) {
      toast.error('更新に失敗しました');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="credit-payments-page">
      <div className="flex items-center justify-between no-print">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">掛け金管理</h1>
          <p className="text-slate-500 mt-1">仕入れ先別の買掛金を管理</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={() => changeMonth(-1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-lg font-bold w-32 text-center">{monthDisplay}</span>
          <Button variant="outline" size="icon" onClick={() => changeMonth(1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" onClick={() => window.print()}>
            <Printer className="h-4 w-4 mr-2" />印刷
          </Button>
        </div>
      </div>

      <Card className="border-slate-800 bg-slate-900 text-white">
        <CardContent className="pt-5 pb-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CreditCard className="h-6 w-6 text-slate-400" />
              <span className="text-lg font-medium">{monthDisplay} 掛け金合計</span>
              <Badge variant="outline" className="border-slate-600 text-slate-300">{data.suppliers.length}社</Badge>
            </div>
            <span className="font-mono font-bold text-3xl text-amber-400">¥{data.grand_total.toLocaleString()}</span>
          </div>
        </CardContent>
      </Card>

      {data.suppliers.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center text-slate-400">
            <CreditCard className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p>この月の掛け金データはありません</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {data.suppliers.map(supplier => {
            const isExpanded = expandedSupplier === supplier.supplier_id;
            const colorClass = PAYMENT_COLORS[supplier.payment_method] || 'bg-slate-100 text-slate-700 border-slate-300';
            return (
              <Card key={supplier.supplier_id} className="border-slate-200 overflow-hidden">
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => toggleExpand(supplier.supplier_id)}
                  data-testid={`credit-supplier-${supplier.supplier_id}`}
                >
                  <div className="flex items-center gap-3">
                    <Building2 className="h-5 w-5 text-slate-400" />
                    <span className="font-bold text-base">{supplier.supplier_name}</span>
                    <Badge className={`border ${colorClass} text-xs`}>{supplier.payment_method}</Badge>
                    <span className="text-xs text-slate-400">{supplier.days.length}日分</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono font-bold text-xl">¥{supplier.total.toLocaleString()}</span>
                    {isExpanded ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-slate-200 bg-slate-50">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200 text-left text-slate-500">
                          <th className="py-2 px-4 font-medium">日付</th>
                          <th className="py-2 px-4 font-medium text-right">8%税率分</th>
                          <th className="py-2 px-4 font-medium text-right">10%税率分</th>
                          <th className="py-2 px-4 font-medium text-right">合計</th>
                          <th className="py-2 px-4 font-medium text-center w-20 no-print">編集</th>
                        </tr>
                      </thead>
                      <tbody>
                        {supplier.days.map(day => {
                          const total8 = day.items.reduce((s, i) => s + (i.amount_8 || 0), 0);
                          const total10 = day.items.reduce((s, i) => s + (i.amount_10 || 0), 0);
                          const isEditing = editingRow?.supplierId === supplier.supplier_id && editingRow?.date === day.date;
                          const hasEdits = day.items.some(i => i.is_edited);
                          // 元の金額
                          const orig8 = day.items.reduce((s, i) => s + (i.original_amount_8 ?? i.amount_8 ?? 0), 0);
                          const orig10 = day.items.reduce((s, i) => s + (i.original_amount_10 ?? i.amount_10 ?? 0), 0);
                          const origTotal = day.items.reduce((s, i) => s + (i.original_amount ?? i.amount ?? 0), 0);

                          if (isEditing) {
                            return (
                              <tr key={day.date} className="border-b border-slate-100 bg-amber-50">
                                <td className="py-2 px-4">
                                  <div className="flex items-center gap-2">
                                    <Calendar className="h-3.5 w-3.5 text-slate-400" />
                                    {format(new Date(day.date), 'M/d(E)', { locale: ja })}
                                  </div>
                                </td>
                                <td className="py-2 px-4 text-right">
                                  <Input type="number" value={editValues.amount_8} onChange={(e) => setEditValues(prev => ({...prev, amount_8: e.target.value}))} className="w-28 text-right ml-auto h-8" />
                                </td>
                                <td className="py-2 px-4 text-right">
                                  <Input type="number" value={editValues.amount_10} onChange={(e) => setEditValues(prev => ({...prev, amount_10: e.target.value}))} className="w-28 text-right ml-auto h-8" />
                                </td>
                                <td className="py-2 px-4 text-right font-mono font-bold">
                                  ¥{((parseFloat(editValues.amount_8) || 0) + (parseFloat(editValues.amount_10) || 0)).toLocaleString()}
                                </td>
                                <td className="py-2 px-4 text-center no-print">
                                  <div className="flex gap-1 justify-center">
                                    <Button size="icon" className="h-7 w-7" onClick={() => saveEdit(supplier.supplier_id)}><Check className="h-3.5 w-3.5" /></Button>
                                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={cancelEdit}><X className="h-3.5 w-3.5" /></Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          }

                          return (
                            <tr key={day.date} className={`border-b border-slate-100 ${hasEdits ? 'bg-orange-50' : ''}`}>
                              <td className="py-2 px-4">
                                <div className="flex items-center gap-2">
                                  <Calendar className="h-3.5 w-3.5 text-slate-400" />
                                  {format(new Date(day.date), 'M/d(E)', { locale: ja })}
                                  {hasEdits && <Badge variant="outline" className="text-xs border-orange-300 text-orange-600">編集済</Badge>}
                                </div>
                              </td>
                              <td className="py-2 px-4 text-right font-mono text-slate-600">
                                {hasEdits && orig8 !== total8 && (
                                  <span className="line-through text-slate-400 mr-2 text-xs">¥{orig8.toLocaleString()}</span>
                                )}
                                {total8 > 0 ? `¥${total8.toLocaleString()}` : '-'}
                              </td>
                              <td className="py-2 px-4 text-right font-mono text-slate-600">
                                {hasEdits && orig10 !== total10 && (
                                  <span className="line-through text-slate-400 mr-2 text-xs">¥{orig10.toLocaleString()}</span>
                                )}
                                {total10 > 0 ? `¥${total10.toLocaleString()}` : '-'}
                              </td>
                              <td className="py-2 px-4 text-right font-mono font-bold">
                                {hasEdits && origTotal !== day.amount && (
                                  <span className="line-through text-slate-400 mr-2 text-xs">¥{origTotal.toLocaleString()}</span>
                                )}
                                ¥{day.amount.toLocaleString()}
                              </td>
                              <td className="py-2 px-4 text-center no-print">
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); startEdit(supplier.supplier_id, day); }}>
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="bg-slate-100 font-bold">
                          <td className="py-2 px-4">合計</td>
                          <td className="py-2 px-4 text-right font-mono">
                            ¥{supplier.days.reduce((s, d) => s + d.items.reduce((ss, i) => ss + (i.amount_8 || 0), 0), 0).toLocaleString()}
                          </td>
                          <td className="py-2 px-4 text-right font-mono">
                            ¥{supplier.days.reduce((s, d) => s + d.items.reduce((ss, i) => ss + (i.amount_10 || 0), 0), 0).toLocaleString()}
                          </td>
                          <td className="py-2 px-4 text-right font-mono text-lg">¥{supplier.total.toLocaleString()}</td>
                          <td className="no-print"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <style>{`
        @media print {
          aside, header, nav, .no-print { display: none !important; }
          html, body { height: auto !important; overflow: visible !important; margin: 0 !important; padding: 0 !important; background: white !important; }
          * { background: transparent !important; }
          [data-testid="app-layout"] > main { margin-left: 0 !important; padding: 0 !important; min-height: 0 !important; display: block !important; }
          [data-testid="credit-payments-page"] { padding: 5mm !important; }
        }
        @page { size: A4 portrait; margin: 10mm; }
      `}</style>
    </div>
  );
};
